import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useDispatch, useSelector } from 'react-redux';
import { FC, createContext, useContext, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';

import { setModal } from 'modules/_shared/redux/modalActions';
import { addFolder, updateFolder } from 'modules/documents/redux/folderActions';

import { Folder } from 'types';

import actionTypes from 'constants/actionTypes';

import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import { badgeStyles } from 'modules/_shared/constants/badgeStyles';
import { AddFolderModalContextType } from '../types/AddFolderModalConextType';
import { addFolderModalInitValue } from '../constants/addFolderModalInitValue';

const AddFolderModalContext = createContext<AddFolderModalContextType>(
  addFolderModalInitValue
);

type AddFolderModalProviderProps = {
  children: React.ReactNode; // eslint-disable-line
  currentFolder?: Folder;
  modalActionType?: string;
};

export const AddFolderModalProvider: FC<AddFolderModalProviderProps> = ({
  children,
  currentFolder,
  modalActionType = actionTypes.ADD,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const [name, setName] = useState<string>(
    modalActionType === actionTypes.EDIT ? currentFolder?.name || '' : ''
  );
  const [style, setStyle] = useState<string>(
    currentFolder?.style || badgeStyles[0]
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeName = (newName: string) => {
    setName(newName);
  };

  const handleChangeStyle = (newStyle: string) => {
    setStyle(newStyle);
  };

  const handleAddFolder = async () => {
    const folderData: Partial<Folder> = {
      name,
      style,
      society: actualSociety?._id,
      parentFolder: currentFolder?._id,
    };

    await store.dispatch(addFolder({ folderData }));
  };

  const handleEditFolder = async () => {
    if (!currentFolder?._id) {
      handleCloseModal();
      return;
    }

    const updateFolderData: Partial<Folder> = {
      name,
      style,
    };

    await store.dispatch(
      updateFolder({ folderId: currentFolder._id, updateFolderData })
    );
  };

  const handleSaveFolder = async () => {
    try {
      setIsLoading(true);

      if (modalActionType === actionTypes.ADD) {
        await handleAddFolder();
      } else {
        await handleEditFolder();
      }

      const successMessage =
        modalActionType === actionTypes.EDIT
          ? t('FolderUpdatedSuccessfully')
          : t('FolderAddedSuccessfully');

      successSwal(successMessage);

      setIsLoading(false);
      handleCloseModal();
    } catch (error) {
      setIsLoading(false);
      const errorMessage =
        modalActionType === actionTypes.EDIT
          ? t('ErrorUpdatingFolder')
          : t('ErrorAddingFolder');

      errorSwal(errorMessage);
      handleCloseModal();
    }
  };

  return (
    <AddFolderModalContext.Provider
      value={{
        data: {
          name,
          style,
          currentFolder,
          modalActionType,
          isLoading,
        },
        actions: {
          handleChangeName,
          handleChangeStyle,
          handleSaveFolder,
          handleCloseModal,
        },
      }}
    >
      {children}
    </AddFolderModalContext.Provider>
  );
};

export const useAddFolderModal = () => {
  const context = useContext(AddFolderModalContext);
  if (!context) {
    throw new Error(
      'useAddFolderModal must be used within a AddFolderModalProvider'
    );
  }
  return context;
};
